"use client";

import * as React from "react";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import {
    DayPicker,
    getDefaultClassNames,
    NextMonthButton as DefaultNextMonthButton,
    PreviousMonthButton as DefaultPreviousMonthButton,
    type DayButton,
    type Locale,
} from "react-day-picker";

import { cn } from "./utils";
import { Button, buttonVariants } from "./button";

function formatCalendarDayTestId(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `calendar-day-${year}-${month}-${day}`;
}

function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    captionLayout = "label",
    buttonVariant = "ghost",
    locale,
    formatters,
    components,
    ...props
}: React.ComponentProps<typeof DayPicker> & {
    buttonVariant?: React.ComponentProps<typeof Button>["variant"];
}) {
    const defaultClassNames = getDefaultClassNames();

    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn(
                "group/calendar bg-background [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent p-3 [--cell-size:2.25rem]",
                String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
                String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
                className
            )}
            captionLayout={captionLayout}
            locale={locale}
            formatters={{
                formatMonthDropdown: (date) => date.toLocaleString(locale?.code, { month: "short" }),
                ...formatters,
            }}
            classNames={{
                root: cn("w-fit", defaultClassNames.root),
                months: cn("relative flex md:flex-row flex-col gap-4", defaultClassNames.months),
                month: cn("flex flex-col gap-4 w-full", defaultClassNames.month),
                nav: cn(
                    "top-0 absolute inset-x-0 flex justify-between items-center gap-1 w-full",
                    defaultClassNames.nav
                ),
                button_previous: cn(
                    buttonVariants({ variant: buttonVariant, size: "icon" }),
                    "shrink-0 p-0 select-none aria-disabled:opacity-50",
                    defaultClassNames.button_previous
                ),
                button_next: cn(
                    buttonVariants({ variant: buttonVariant, size: "icon" }),
                    "shrink-0 p-0 select-none aria-disabled:opacity-50",
                    defaultClassNames.button_next
                ),
                month_caption: cn("flex justify-center items-center px-1 w-full h-10", defaultClassNames.month_caption),
                dropdowns: cn(
                    "flex justify-center items-center gap-1.5 w-full h-10 font-medium text-sm",
                    defaultClassNames.dropdowns
                ),
                dropdown_root: cn(
                    "relative shadow-sm border border-input has-focus:border-indigo-600 rounded-md has-focus:ring-[3px] has-focus:ring-indigo-600/35",
                    defaultClassNames.dropdown_root
                ),
                dropdown: cn("absolute inset-0 bg-popover opacity-0", defaultClassNames.dropdown),
                caption_label: cn(
                    "font-medium select-none",
                    captionLayout === "label"
                        ? "text-sm"
                        : "flex h-8 items-center gap-1 rounded-md pl-2 pr-1 text-sm [&>svg]:size-3.5 [&>svg]:text-muted-foreground",
                    defaultClassNames.caption_label
                ),
                table: "w-full border-collapse",
                weekdays: cn("flex", defaultClassNames.weekdays),
                weekday: cn(
                    "flex-1 rounded-md font-normal text-[0.8rem] text-muted-foreground select-none",
                    defaultClassNames.weekday
                ),
                week: cn("flex mt-2 w-full", defaultClassNames.week),
                week_number_header: cn("w-10 select-none shrink-0", defaultClassNames.week_number_header),
                week_number: cn("text-[0.8rem] text-muted-foreground select-none", defaultClassNames.week_number),
                day: cn(
                    "group/day relative p-1 [&:last-child[data-selected=true]_button]:rounded-r-md [&:first-child[data-selected=true]_button]:rounded-l-md w-full min-w-0 h-full min-h-10 aspect-square text-center select-none",
                    props.showWeekNumber
                        ? "[&:nth-child(2)[data-selected=true]_button]:rounded-l-md"
                        : "[&:first-child[data-selected=true]_button]:rounded-l-md",
                    defaultClassNames.day
                ),
                range_start: cn("bg-indigo-50 dark:bg-indigo-950/50 rounded-l-md", defaultClassNames.range_start),
                range_middle: cn("bg-indigo-50 dark:bg-indigo-950/40 rounded-none", defaultClassNames.range_middle),
                range_end: cn("bg-indigo-50 dark:bg-indigo-950/50 rounded-r-md", defaultClassNames.range_end),
                today: cn(
                    "bg-indigo-50 data-[selected=true]:bg-transparent dark:bg-indigo-950/50 rounded-md data-[selected=true]:rounded-none ring-1 ring-indigo-200 data-[selected=true]:ring-0 dark:ring-indigo-800 text-indigo-800 dark:text-indigo-100",
                    defaultClassNames.today
                ),
                outside: cn("text-muted-foreground aria-selected:text-muted-foreground", defaultClassNames.outside),
                disabled: cn("opacity-50 text-muted-foreground", defaultClassNames.disabled),
                hidden: cn("invisible", defaultClassNames.hidden),
                ...classNames,
            }}
            components={{
                Root: ({ className: rootClassName, rootRef, ...rootProps }) => (
                    <div
                        data-slot="calendar"
                        data-testid="calendar"
                        ref={rootRef}
                        className={cn(rootClassName)}
                        {...rootProps}
                    />
                ),
                PreviousMonthButton: (props) => (
                    <DefaultPreviousMonthButton {...props} data-testid="calendar-previous-month" />
                ),
                NextMonthButton: (props) => <DefaultNextMonthButton {...props} data-testid="calendar-next-month" />,
                Chevron: ({ className: chevronClassName, orientation, ...chevronProps }) => {
                    if (orientation === "left") {
                        return <ChevronLeftIcon className={cn("size-4", chevronClassName)} {...chevronProps} />;
                    }
                    if (orientation === "right") {
                        return <ChevronRightIcon className={cn("size-4", chevronClassName)} {...chevronProps} />;
                    }
                    return <ChevronDownIcon className={cn("size-4", chevronClassName)} {...chevronProps} />;
                },
                DayButton: ({ ...dayButtonProps }) => <CalendarDayButton locale={locale} {...dayButtonProps} />,
                WeekNumber: ({ children, ...weekProps }) => (
                    <td {...weekProps}>
                        <div className="flex justify-center items-center w-10 h-10 text-center shrink-0">
                            {children}
                        </div>
                    </td>
                ),
                ...components,
            }}
            {...props}
        />
    );
}

function CalendarDayButton({
    className,
    day,
    modifiers,
    locale,
    ...props
}: React.ComponentProps<typeof DayButton> & { locale?: Partial<Locale> }) {
    const defaultClassNames = getDefaultClassNames();

    const ref = React.useRef<HTMLButtonElement>(null);
    React.useEffect(() => {
        if (modifiers.focused) ref.current?.focus();
    }, [modifiers.focused]);

    return (
        <Button
            ref={ref}
            variant="ghost"
            size="icon"
            data-testid={formatCalendarDayTestId(day.date)}
            data-day={day.date.toLocaleDateString(locale?.code)}
            data-selected-single={
                modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle
            }
            data-range-start={modifiers.range_start}
            data-range-end={modifiers.range_end}
            data-range-middle={modifiers.range_middle}
            className={cn(
                "z-10 group-data-[focused=true]/day:z-10 relative group-data-[focused=true]/day:relative flex flex-col gap-0.5 data-[range-end=true]:bg-indigo-700 data-[range-end=true]:hover:bg-indigo-800 data-[range-middle=true]:bg-indigo-100 data-[range-middle=true]:hover:bg-indigo-200 data-[range-start=true]:bg-indigo-700 data-[range-start=true]:hover:bg-indigo-800 data-[selected-single=true]:bg-indigo-700 data-[selected-single=true]:hover:bg-indigo-800 dark:data-[range-end=true]:bg-indigo-700 dark:data-[range-middle=true]:bg-indigo-900/60 dark:data-[range-middle=true]:hover:bg-indigo-800/70 dark:data-[range-start=true]:bg-indigo-700 dark:data-[selected-single=true]:bg-indigo-700 [&>span]:opacity-80 p-0 border-0 dark:group-data-[focused=true]/day:border-indigo-600 group-data-[focused=true]/day:border-2 group-data-[focused=true]/day:border-indigo-700 data-[range-end=true]:rounded-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md dark:group-data-[focused=true]/day:ring-indigo-600/40 group-data-[focused=true]/day:ring-[3px] group-data-[focused=true]/day:ring-indigo-700/35 aspect-square font-normal [&>span]:text-indigo-50 data-[range-end=true]:text-white data-[range-middle=true]:text-indigo-900 data-[range-start=true]:text-white data-[selected-single=true]:text-white dark:[&>span]:text-indigo-100 dark:data-[range-middle=true]:text-indigo-50 [&>span]:text-xs leading-none",
                defaultClassNames.day,
                className,
                "h-full min-h-10 w-full max-w-none rounded-md"
            )}
            {...props}
        />
    );
}

export type CalendarProps = React.ComponentProps<typeof Calendar>;

export { Calendar, CalendarDayButton };
